import { useState, useEffect } from 'react';
import { Mail, Download, Users, CheckCircle, XCircle, Filter, Search, Calendar, RefreshCw } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { formatDistanceToNow } from '../utils/date-utils';
import { devError } from '../utils/logger';
import { getSubscriptionDisplayName } from '../utils/accessPolicy';
import { fetchWithAuth } from '../utils/api';


interface EmailListUser {
  id: string;
  name: string;
  email: string;
  userType: 'talent' | 'employer';
  subscription: 'free' | 'plus' | 'employer' | 'employer_plus';
  marketingEmailsConsent: boolean;
  marketingEmailsConsentDate: string | null;
  createdAt: string;
  county?: string;
  categories?: string[];
}

interface AdminEmailListsProps {
  accessToken: string;
  subAdminId?: string; // ✅ NEW: Optional sub-admin session ID
}

export function AdminEmailLists({ accessToken, subAdminId }: AdminEmailListsProps) {
  const [users, setUsers] = useState<EmailListUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [consentFilter, setConsentFilter] = useState<'all' | 'consented' | 'not_consented'>('all');
  const [userTypeFilter, setUserTypeFilter] = useState<'all' | 'talent' | 'employer'>('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState<'all' | 'free' | 'plus' | 'employer' | 'employer_plus'>('all');

  useEffect(() => {
    fetchEmailLists();
  }, []);

  const fetchEmailLists = async () => {
    setLoading(true);
    try {
      // ✅ Build headers with sub-admin session if present
      const headers: HeadersInit = {
        'Authorization': `Bearer ${accessToken}`
      };
      if (subAdminId) {
        headers['X-SubAdmin-Session'] = subAdminId;
      }

      const response = await fetchWithAuth(
        `${API_BASE}/admin/email-lists`,
        { headers }
      );

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        toast.error('Kunne ikke laste e-postlister');
      }
    } catch (error) {
      devError('Error fetching email lists:', error);
      toast.error('Noe gikk galt');
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    // Search filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      user.name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.county?.toLowerCase().includes(searchLower);

    if (searchTerm && !matchesSearch) return false;

    // Consent filter
    if (consentFilter === 'consented' && !user.marketingEmailsConsent) return false;
    if (consentFilter === 'not_consented' && user.marketingEmailsConsent) return false;

    // User type filter
    if (userTypeFilter !== 'all' && user.userType !== userTypeFilter) return false;

    // Subscription filter
    if (subscriptionFilter !== 'all' && user.subscription !== subscriptionFilter) return false;

    return true;
  });

  // Export filtered users as CSV
  const handleExport = () => {
    setExporting(true);
    
    try {
      // Create CSV header
      const headers = ['Navn', 'E-post', 'Brukertype', 'Abonnement', 'Markedsføring samtykke', 'Samtykke dato', 'Registrert', 'Fylke', 'Kategorier'];
      
      // Create CSV rows
      const rows = filteredUsers.map(user => [
        user.name || '',
        user.email || '',
        user.userType === 'talent' ? 'Talent' : 'Arbeidsgiver',
        getSubscriptionDisplayName(user.subscription),
        user.marketingEmailsConsent ? 'Ja' : 'Nei',
        user.marketingEmailsConsentDate ? new Date(user.marketingEmailsConsentDate).toLocaleDateString('no-NO') : 'Aldri endret',
        new Date(user.createdAt).toLocaleDateString('no-NO'),
        user.county || '',
        user.categories?.join(', ') || ''
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with filters and date
      const filterSuffix = consentFilter === 'consented' ? '-med-samtykke' : 
                          consentFilter === 'not_consented' ? '-uten-samtykke' : '';
      link.download = `talentfunn-epostliste${filterSuffix}-${new Date().toISOString().split('T')[0]}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`${filteredUsers.length} brukere eksportert`);
    } catch (error) {
      devError('Export error:', error);
      toast.error('Kunne ikke eksportere');
    } finally {
      setExporting(false);
    }
  };

  // Stats
  const totalUsers = users.length;
  const consentedUsers = users.filter(u => u.marketingEmailsConsent).length;
  const notConsentedUsers = totalUsers - consentedUsers;
  const consentPercentage = totalUsers > 0 ? ((consentedUsers / totalUsers) * 100).toFixed(1) : '0';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader size="lg" className="text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Mail className="w-8 h-8" />
            <h2 className="text-2xl font-bold">E-postlister</h2>
          </div>
          <button
            onClick={fetchEmailLists}
            className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-all"
            title="Oppdater"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-5 h-5" />
              <p className="text-sm opacity-90">Totalt brukere</p>
            </div>
            <p className="text-3xl font-bold">{totalUsers}</p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-5 h-5" />
              <p className="text-sm opacity-90">Med samtykke</p>
            </div>
            <p className="text-3xl font-bold">{consentedUsers}</p>
            <p className="text-sm opacity-75 mt-1">{consentPercentage}% av totalt</p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="w-5 h-5" />
              <p className="text-sm opacity-90">Uten samtykke</p>
            </div>
            <p className="text-3xl font-bold">{notConsentedUsers}</p>
            <p className="text-sm opacity-75 mt-1">{(100 - parseFloat(consentPercentage)).toFixed(1)}% av totalt</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Filtre og søk</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Søk navn, e-post, fylke..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Consent Filter */}
          <select
            value={consentFilter}
            onChange={(e) => setConsentFilter(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">Alle (samtykke)</option>
            <option value="consented">✓ Med samtykke</option>
            <option value="not_consented">✗ Uten samtykke</option>
          </select>

          {/* User Type Filter */}
          <select
            value={userTypeFilter}
            onChange={(e) => setUserTypeFilter(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">Alle brukertyper</option>
            <option value="talent">Talent</option>
            <option value="employer">Arbeidsgiver</option>
          </select>

          {/* Subscription Filter */}
          <select
            value={subscriptionFilter}
            onChange={(e) => setSubscriptionFilter(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">Alle abonnementer</option>
            <option value="free">Gratis</option>
            <option value="plus">Talentfunn Pluss</option>
            <option value="employer">Arbeidsgiver Basis</option>
            <option value="employer_plus">Arbeidsgiver Pluss</option>
          </select>
        </div>

        {/* Export Button */}
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Viser <strong>{filteredUsers.length}</strong> av <strong>{totalUsers}</strong> brukere
          </p>
          <button
            onClick={handleExport}
            disabled={exporting || filteredUsers.length === 0}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {exporting ? (
              <>
                <Loader size="sm" />
                Eksporterer...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Eksporter som CSV ({filteredUsers.length})
              </>
            )}
          </button>
        </div>
      </div>

      {/* GDPR Notice */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            <p className="font-semibold text-blue-900 mb-1">GDPR-kompatibel eksport</p>
            <p>
              E-postlister eksporteres kun for brukere som matcher filtrene. 
              Brukere <strong>uten samtykke</strong> kan kun kontaktes med <strong>transaksjonelle e-poster</strong> 
              (kontobekreftelser, kvitteringer, prisendringer).
              Brukere <strong>med samtykke</strong> kan motta markedsføring (nyhetsbrev, kampanjer, tips).
            </p>
          </div>
        </div>
      </div>

      {/* User List Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Bruker</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Abonnement</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Markedsføring</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Samtykke dato</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Registrert</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    Ingen brukere matcher filtrene
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{user.name || 'Ingen navn'}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        {user.county && (
                          <p className="text-xs text-gray-400 mt-0.5">📍 {user.county}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.userType === 'talent' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.userType === 'talent' ? 'Talent' : 'Arbeidsgiver'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.subscription === 'free' 
                          ? 'bg-gray-100 text-gray-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {getSubscriptionDisplayName(user.subscription)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {user.marketingEmailsConsent ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className={`text-sm font-medium ${
                          user.marketingEmailsConsent ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {user.marketingEmailsConsent ? 'Ja' : 'Nei'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {user.marketingEmailsConsentDate ? (
                        <div>
                          <p>{new Date(user.marketingEmailsConsentDate).toLocaleDateString('no-NO')}</p>
                          <p className="text-xs text-gray-400">
                            {formatDistanceToNow(user.marketingEmailsConsentDate)}
                          </p>
                        </div>
                      ) : (
                        <span className="text-gray-400">Aldri endret</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div>
                        <p>{new Date(user.createdAt).toLocaleDateString('no-NO')}</p>
                        <p className="text-xs text-gray-400">
                          {formatDistanceToNow(user.createdAt)}
                        </p>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
